import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';
import type { Arbeitspaket, KanbanBoard, KanbanCard, TaskState } from '@pm/shared';

/** Boards eines Arbeitspakets (seq DESC, jeweils mit cards[]). */
const KEY = (pid: string, apId: string) => ['projects', pid, 'arbeitspakete', apId, 'kanban'] as const;
/** Projektweites Board-Aggregat (scope=project). */
const PROJECT_KEY = (pid: string) => ['projects', pid, 'kanban', 'project-scope'] as const;

/** Nach einer Karten-Mutation: AP-Board-Cache UND Projekt-Aggregat invalidieren,
 *  damit beide Darstellungsmodi (T-071) konsistent bleiben. */
function invalidateBoards(qc: ReturnType<typeof useQueryClient>, pid: string, apId: string) {
  void qc.invalidateQueries({ queryKey: KEY(pid, apId) });
  void qc.invalidateQueries({ queryKey: PROJECT_KEY(pid) });
}

export function useApKanbanBoards(projectId: string | undefined, apId: string | undefined) {
  return useQuery({
    queryKey: KEY(projectId ?? '', apId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/arbeitspakete/${apId}/kanban/boards`),
    enabled: !!projectId && !!apId,
  });
}

/** „Neues Board": friert das aktive Board ein, kopiert dessen Karten 1:1 + seedet die R-Karte (server-side). */
export function useCreateKanbanBoard(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (apId: string) =>
      apiClient.post(`/api/v1/projects/${pid}/arbeitspakete/${apId}/kanban/boards`, {}),
    onSuccess: (_res, apId) => invalidateBoards(qc, pid, apId),
  });
}

export function useRenameKanbanBoard(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { apId: string; boardId: string; label: string }) =>
      apiClient.patch(`/api/v1/projects/${pid}/kanban/boards/${vars.boardId}`, { label: vars.label }),
    onSuccess: (_res, vars) => qc.invalidateQueries({ queryKey: KEY(pid, vars.apId) }),
  });
}

export function useCreateKanbanCard(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { apId: string; boardId: string; data: Partial<KanbanCard> }) =>
      apiClient.post(`/api/v1/projects/${pid}/kanban/boards/${vars.boardId}/cards`, vars.data),
    onSuccess: (_res, vars) => invalidateBoards(qc, pid, vars.apId),
  });
}

export function useUpdateKanbanCard(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { apId: string; cardId: string; data: Partial<Pick<KanbanCard, 'state' | 'title' | 'description' | 'person' | 'hidden' | 'seq'>> }) =>
      apiClient.patch(`/api/v1/projects/${pid}/kanban/cards/${vars.cardId}`, vars.data),
    onSuccess: (_res, vars) => invalidateBoards(qc, pid, vars.apId),
  });
}

export function useDeleteKanbanCard(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { apId: string; cardId: string }) =>
      apiClient.delete(`/api/v1/projects/${pid}/kanban/cards/${vars.cardId}`),
    onSuccess: (_res, vars) => invalidateBoards(qc, pid, vars.apId),
  });
}

/** Unwrap-Helfer: API liefert { data: KanbanBoard[] }. */
export function unwrapBoards(resp: unknown): KanbanBoard[] {
  return (resp as { data?: KanbanBoard[] } | undefined)?.data ?? [];
}

/** Projektweiter Board-Eintrag (scope=project): KanbanBoard + AP-Herkunft fürs Chip. */
export interface ProjectKanbanBoard extends KanbanBoard {
  ap_nr: string;
  ap_titel: string | null;
}

/** Alle aktiven (frozen=false) AP-Boards eines Projekts flach — für den
 *  projektweiten Kanban-Modus (T-071). Jeder Eintrag trägt ap_nr/ap_titel. */
export function useProjectKanbanBoards(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: PROJECT_KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/kanban/boards?scope=project&active=1`),
    enabled: !!projectId && enabled,
  });
}

/** Invalidiert sowohl die AP-Board-Caches als auch das Projekt-Aggregat — für
 *  Karten-Mutationen, die im Projekt-Modus ausgelöst werden. */
export function unwrapProjectBoards(resp: unknown): ProjectKanbanBoard[] {
  return (resp as { data?: ProjectKanbanBoard[] } | undefined)?.data ?? [];
}

/** Sichtbare Kanban-Spalten. „Gestartet" (STARTED, = In Arbeit) und „Abgebrochen"
 *  (CANCELED) wurden auf Kundenwunsch entfernt. Der TaskState-Enum im Backend bleibt
 *  unverändert — Bestandskarten werden über normalizeKanbanState abgebildet. */
export const KANBAN_STATES: TaskState[] = ['PLANNED', 'IN_PROGRESS', 'COMPLETED'];

/** Bildet einen (evtl. alten) TaskState auf die verbleibende Spalte ab, damit keine
 *  Karte verschwindet: STARTED → IN_PROGRESS (dasselbe wie „In Arbeit"),
 *  CANCELED → COMPLETED (Endzustand). */
export function normalizeKanbanState(state: TaskState): TaskState {
  if (state === 'STARTED') return 'IN_PROGRESS';
  if (state === 'CANCELED') return 'COMPLETED';
  return state;
}

/** Distinkte, alphabetisch sortierte Personen aus den RACI-Feldern (R/A/C/I) der
 *  übergebenen Arbeitspakete — Optionsliste für den Personen-Picker an Kanban-Karten
 *  (P-14). RACI-Felder sind Freitext, daher reine String-Deduplikation (trim). */
export function getRaciPersons(aps: Pick<Arbeitspaket, 'raci_r' | 'raci_a' | 'raci_c' | 'raci_i'>[]): string[] {
  const persons = new Set<string>();
  for (const ap of aps) {
    for (const value of [ap.raci_r, ap.raci_a, ap.raci_c, ap.raci_i]) {
      const trimmed = value?.trim();
      if (trimmed) persons.add(trimmed);
    }
  }
  return [...persons].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}
